import { useMemo } from 'react'
import { useGetOrgCredentials } from '@/service-library/hooks/org-credentials'
import { Organization } from '@/types/organizations'
import { Project } from '@/types/projects'
import { useOrganizationsContext } from '@/components/organizations/OrganizationsProvider'

type UseCredentialedDataProviderOrgsOptions = {
  project: Project
  dataProviderOrgs: Organization[]
  enabled?: boolean
}

/**
 * The companies that can answer for this project's accounting system.
 *
 * A login usually sits on a parent rather than on the company itself, so a
 * company counts if it or anything above it in the project's tree holds one.
 */
export default function useCredentialedDataProviderOrgs({
  project,
  dataProviderOrgs,
  enabled = true,
}: UseCredentialedDataProviderOrgsOptions) {
  const hasOrgCredentialType = !!project.org_credential_type_id

  const { orgCredentials, isFetching: isFetchingOrgCredentials } =
    useGetOrgCredentials({
      filters: {
        type_id: project.org_credential_type_id || '',
        fields__only: 'id,org_id',
        limit: '1000',
      },
      enabled: enabled && hasOrgCredentialType,
    })

  const { organizations: allOrganizations, isFetchingAll: isFetchingAllOrgs } =
    useOrganizationsContext()

  const credentialedDataProviderOrgs = useMemo(() => {
    const credentialedOrgIds = new Set(
      orgCredentials.map((credential) => credential.org_id),
    )
    if (credentialedOrgIds.size === 0) return []
    const parentOrgIdById = new Map(
      allOrganizations.map((org) => [org.id, org.parent_org_id]),
    )
    return dataProviderOrgs.filter((dataProviderOrg) => {
      let currentOrgId: string | null | undefined = dataProviderOrg.id
      while (currentOrgId) {
        if (credentialedOrgIds.has(currentOrgId)) return true
        currentOrgId = parentOrgIdById.get(currentOrgId)
      }
      return false
    })
  }, [allOrganizations, dataProviderOrgs, orgCredentials])

  return {
    credentialedDataProviderOrgs,
    isLoading: isFetchingOrgCredentials || isFetchingAllOrgs,
  }
}
